import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Checkbox, Input, Popover, Select, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState, type JSX, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../api/client';
import { operationRequestsApi } from '../../api/endpoints';
import { invalidateMoneyData } from '../../api/queryKeys';
import type {
  ExpenseCategory,
  PaymentMethod,
  PendingColumn,
  StoreDayGridPending,
  UpdateOperationRequestBody,
} from '../../api/types';
import { AmountInput } from '../../components/AmountInput';
import { useApprovalActions } from '../../hooks/useApprovalActions';
import { UNUSUAL_NEGATIVE } from '../../lib/balanceColors';
import { formatKopeks, kopeksToRubString, rubToKopeks } from '../../lib/money';

/** i18n key of the column heading, reused as the popover title. */
const COLUMN_TITLE: Record<PendingColumn, string> = {
  cash: 'daygrid.colCash',
  terminal: 'daygrid.colTerminal',
  salary: 'daygrid.colSalary',
  expenses: 'daygrid.colExpenses',
};

/** The inline «изменить» form for one pending row. */
function EditRow({
  req,
  categories,
  salaryCategoryId,
  onDone,
  onCancel,
}: {
  req: StoreDayGridPending;
  categories: ExpenseCategory[];
  salaryCategoryId: string | null;
  onDone: (approve: boolean) => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(kopeksToRubString(req.amount));
  const [comment, setComment] = useState(req.comment ?? '');
  const [method, setMethod] = useState<PaymentMethod | null>(req.paymentMethod);
  const [categoryId, setCategoryId] = useState<string | null>(req.categoryId);
  const [deduct, setDeduct] = useState(req.deductFromStoreBalance);

  const save = useMutation({
    mutationFn: (body: UpdateOperationRequestBody) => operationRequestsApi.update(req.id, body),
    onError: (error: Error) => message.error(apiErrorMessage(error) ?? t('common.requestError')),
  });

  const submit = async (approve: boolean): Promise<void> => {
    const kopeks = amount ? rubToKopeks(amount) : 0;
    if (kopeks <= 0) {
      message.error(t('common.amountInvalid'));
      return;
    }
    const body: UpdateOperationRequestBody = {
      amount: kopeks,
      comment: comment.trim(),
      ...(req.type === 'STORE_PAYMENT' && method ? { paymentMethod: method } : {}),
      ...(req.type === 'EXPENSE' && categoryId ? { categoryId } : {}),
      ...(req.type === 'EXPENSE' ? { deductFromStoreBalance: deduct } : {}),
    };
    try {
      await save.mutateAsync(body);
    } catch {
      return; // the toast already fired; keep the form open with the input intact
    }
    if (!approve) message.success(t('daygrid.pendingSaved'));
    await onDone(approve);
    // ONE refresh for the whole chain — a refetch BETWEEN the edit and the
    // approve would re-render this row out from under the approve call. Runs
    // even when the approve was refused by the money guard: the edit landed and
    // has to show.
    invalidateMoneyData(queryClient);
  };

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <AmountInput value={amount} onChange={setAmount} autoFocus size="middle" />
      {req.type === 'STORE_PAYMENT' ? (
        <Select
          size="small"
          value={method ?? undefined}
          onChange={setMethod}
          options={[
            { value: 'CASH', label: t('payMethod.CASH') },
            { value: 'TERMINAL', label: t('payMethod.TERMINAL') },
          ]}
        />
      ) : (
        <>
          <Select
            size="small"
            value={categoryId ?? undefined}
            onChange={setCategoryId}
            placeholder={t('common.category')}
            options={categories
              .filter((c) => c.id !== salaryCategoryId || req.column === 'salary')
              .map((c) => ({ value: c.id, label: c.name }))}
          />
          <Checkbox checked={deduct} onChange={(e) => setDeduct(e.target.checked)}>
            <span style={{ fontSize: 12 }}>{t('quick.deductFromStore')}</span>
          </Checkbox>
        </>
      )}
      <Input
        size="small"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={t('common.comment')}
        maxLength={1000}
      />
      <Space size={4} wrap>
        <Button
          type="primary"
          size="small"
          loading={save.isPending}
          onClick={() => void submit(true)}
        >
          {t('daygrid.pendingSaveAndApprove')}
        </Button>
        <Button size="small" loading={save.isPending} onClick={() => void submit(false)}>
          {t('common.save')}
        </Button>
        <Button size="small" type="text" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </Space>
    </div>
  );
}

/**
 * The «ожидает» line under an approved amount in the store jadval, and the
 * popover behind it.
 *
 * A PENDING seller entry moves no balance, so it is shown BESIDE the day's
 * approved money — never added to it — in the column it will land in once
 * approved. Tapping the orange line opens the queue for exactly that day and
 * column: approve, correct-then-approve, or reject, without leaving the table.
 *
 * Only mounted for cells that actually have pending money (the caller checks),
 * so the month's empty cells cost nothing. A seller gets the amount only — the
 * backend sends them no rows: evidence their entry arrived, no buttons.
 */
export function PendingCellPopover({
  amount,
  requests,
  date,
  column,
  categories,
  salaryCategoryId,
  readOnly,
}: {
  amount: number;
  requests: StoreDayGridPending[];
  date: string;
  column: PendingColumn;
  categories: ExpenseCategory[];
  salaryCategoryId: string | null;
  readOnly: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { approve, approveAsync, approveBatch, confirmReject, busyId, approvePending, batchBusy } =
    useApprovalActions();

  const line = (
    <span
      style={{
        color: UNUSUAL_NEGATIVE,
        fontSize: 12,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {t('daygrid.pendingLine', { sum: formatKopeks(amount) })}
      {requests.length > 1 ? ` (${requests.length})` : ''}
    </span>
  );

  // Seller (or an admin whose rows were withheld): show the evidence, no actions.
  if (readOnly || requests.length === 0) return line;

  const closeIfLast = (): void => {
    if (requests.length <= 1) setOpen(false);
    setEditingId(null);
  };

  const content = (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ width: 290, display: 'grid', gap: 10, maxHeight: 320, overflowY: 'auto' }}
    >
      {requests.map((req) => (
        <div key={req.id} style={{ display: 'grid', gap: 4 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Typography.Text strong>{formatKopeks(req.amount)}</Typography.Text>
            {req.deductFromStoreBalance ? (
              <Tag color="volcano">{t('quick.deductFromStore')}</Tag>
            ) : null}
            {req.duplicateOperationId ? (
              <Tag color="red">{t('approvals.duplicateLedgerTag')}</Tag>
            ) : req.duplicateGroupId ? (
              <Tag color="orange">{t('approvals.duplicateTag')}</Tag>
            ) : null}
            {req.duplicateConfirmedAt ? (
              <Tag color="blue">{t('approvals.duplicateSellerConfirmed')}</Tag>
            ) : null}
          </span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {/* The submit time is what tells two identical rows apart. */}
            {req.createdByName} · {dayjs(req.createdAt).format('HH:mm')}
            {req.categoryName ? ` · ${req.categoryName}` : ''}
            {req.comment ? ` · ${req.comment}` : ''}
          </Typography.Text>
          {editingId === req.id ? (
            <EditRow
              req={req}
              categories={categories}
              salaryCategoryId={salaryCategoryId}
              onCancel={() => setEditingId(null)}
              onDone={async (shouldApprove) => {
                // approveAsync rejects when the money guard fires; its own
                // handler already put the confirm dialog on screen, so this is
                // «not approved», not a failure to report twice.
                if (shouldApprove) await approveAsync(req.id).catch(() => undefined);
                closeIfLast();
              }}
            />
          ) : (
            <Space size={4}>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={busyId === req.id && approvePending}
                disabled={batchBusy}
                onClick={() => {
                  approve(req.id);
                  closeIfLast();
                }}
              >
                {t('approvals.approve')}
              </Button>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => setEditingId(req.id)}
                aria-label={t('daygrid.pendingEdit')}
              />
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                disabled={batchBusy}
                onClick={() => confirmReject(req)}
                aria-label={t('approvals.reject')}
              />
            </Space>
          )}
        </div>
      ))}
      {requests.length > 1 ? (
        <Button
          type="primary"
          size="small"
          block
          loading={batchBusy}
          onClick={() => {
            approveBatch(
              `${date}:${column}`,
              requests.map((r) => r.id),
            );
            setOpen(false);
          }}
        >
          {t('approvals.approveAll')} ({requests.length})
        </Button>
      ) : null}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      destroyTooltipOnHide
      // Body-mounted like the other day-grid popovers: inside the table's
      // horizontal scroll container a locally-mounted popup yanks scrollLeft
      // back to 0 on mobile.
      getPopupContainer={() => document.body}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>
            {dayjs(date).format('DD.MM')} · {t(COLUMN_TITLE[column])}
          </span>
          {/* Explicit close: on a phone there is no «click outside» to be sure of. */}
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label={t('common.close')}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              setEditingId(null);
            }}
          />
        </span>
      }
      content={content}
    >
      <span
        role="button"
        onClick={(e: MouseEvent) => {
          // The row click opens the day drawer; this cell has its own job.
          e.stopPropagation();
          setOpen(true);
        }}
        style={{ cursor: 'pointer', display: 'inline-flex', paddingBlock: 2 }}
      >
        {line}
      </span>
    </Popover>
  );
}
